import { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft, GraduationCap, Store } from "lucide-react";

const SMS_NUMBER_DISPLAY = "+1 775 402-2719";
const SMS_QR_VALUE = "sms:+17754022719?&body=LEARN";

/* Printable counter cards (4x6in). Two variants:
   ?type=academy — recruit sports & trades students into the SMS course ($1 grad reward)
   ?type=guest   — walk-in customers: scan, show at counter, guest wallet arrives by text */
export default function Poster() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [type, setType] = useState(params.get("type") === "guest" ? "guest" : "academy");
  const shop = (params.get("shop") || (type === "guest" ? "DIORS DOUGH" : "THE SPORTS & TRADES ACADEMY")).toUpperCase();
  const guestUrl = `${window.location.origin}/guest`;

  return (
    <div data-testid="poster-page" className="min-h-screen bg-[#0c0a09] text-stone-200 flex flex-col items-center py-10 px-4">
      <style>{`
        @media print {
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .poster-wrap { padding: 0 !important; margin: 0 !important; }
          .poster-card { box-shadow: none !important; }
          @page { size: 4in 6in; margin: 0; }
        }
      `}</style>

      <div className="no-print flex flex-wrap items-center justify-center gap-3 mb-6">
        <a href="/" data-testid="poster-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500 hover:text-amber-500 transition-colors">
          <ArrowLeft size={13} /> bgnzn.org
        </a>
        <div data-testid="poster-type-toggle" className="inline-flex rounded-full border border-stone-700 overflow-hidden">
          <button
            data-testid="poster-type-academy"
            onClick={() => setType("academy")}
            className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2 transition-colors ${type === "academy" ? "bg-amber-500 text-black" : "text-stone-400 hover:text-stone-200"}`}
          >
            <GraduationCap size={12} /> Student card
          </button>
          <button
            data-testid="poster-type-guest"
            onClick={() => setType("guest")}
            className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2 transition-colors ${type === "guest" ? "bg-amber-500 text-black" : "text-stone-400 hover:text-stone-200"}`}
          >
            <Store size={12} /> Guest wallet card
          </button>
        </div>
        <button
          data-testid="poster-print-btn"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 rounded-full bg-amber-500 text-black font-bold text-xs uppercase tracking-wider px-5 py-2.5 hover:bg-amber-400 transition-colors"
        >
          <Printer size={13} /> Print counter card
        </button>
      </div>
      <p className="no-print text-stone-500 text-xs mb-8 font-mono tracking-widest">
        4 × 6 IN COUNTER CARD · CO-BRAND WITH ?shop=YOUR%20SHOP
      </p>

      <div className="poster-wrap">
        {type === "academy" ? (
          <div
            data-testid="poster-card"
            className="poster-card w-[4in] h-[6in] rounded-2xl border-2 border-amber-500/60 bg-gradient-to-b from-stone-900 to-black p-6 flex flex-col items-center text-center shadow-2xl shadow-amber-500/10"
          >
            <p className="font-mono text-[8px] tracking-[0.35em] text-amber-500/80">{shop}</p>
            <h1 className="mt-3 text-[25px] leading-[1.05] font-black text-stone-100" style={{ fontFamily: "Georgia, serif" }}>
              LEARN A TRADE.<br />EARN YOUR<br />FIRST <span className="text-amber-500">DOLLAR</span>
            </h1>
            <p className="mt-3 text-[9.5px] leading-relaxed text-stone-400 max-w-[3in]">
              Sports as a vocation · HVAC · carpentry · wood carving · backhoe & skid steer.
              Seven short lessons by text — graduate and a real <span className="text-amber-500 font-semibold">$1.00</span> lands
              in your new wallet. No bank account needed.
            </p>
            <div className="mt-4 rounded-xl bg-white p-3">
              <QRCodeSVG data-testid="poster-qr" value={SMS_QR_VALUE} size={140} />
            </div>
            <p className="mt-3 text-[11px] font-bold text-stone-100">
              Text <span className="text-amber-500">LEARN</span> to {SMS_NUMBER_DISPLAY}
            </p>
            <p className="mt-auto pt-3 font-mono text-[7px] tracking-[0.3em] text-stone-600">
              BGNZN.ORG · THE BOURGANIZATION INSTITUTE · CHAIN 9940
            </p>
          </div>
        ) : (
          <div
            data-testid="poster-card"
            className="poster-card w-[4in] h-[6in] rounded-2xl border-2 border-amber-500/60 bg-gradient-to-b from-stone-900 to-black p-6 flex flex-col items-center text-center shadow-2xl shadow-amber-500/10"
          >
            <p className="font-mono text-[8px] tracking-[0.35em] text-amber-500/80">WELCOME TO {shop}</p>
            <h1 className="mt-3 text-[25px] leading-[1.05] font-black text-stone-100" style={{ fontFamily: "Georgia, serif" }}>
              PAY WITH<br />YOUR <span className="text-amber-500">PHONE</span>
            </h1>
            <p className="mt-3 text-[9.5px] leading-relaxed text-stone-400 max-w-[3in]">
              No app. No card. No bank account. Scan, show this card at the counter,
              and your guest wallet arrives by text in 30 seconds — cash in,
              one-tap Face ID payments from then on.
            </p>
            <div className="mt-4 rounded-xl bg-white p-3">
              <QRCodeSVG data-testid="poster-guest-qr" value={guestUrl} size={140} />
            </div>
            <p className="mt-3 text-[10px] font-bold text-stone-100 leading-snug">
              Scan → show at counter →<br /><span className="text-amber-500">wallet arrives by text</span>
            </p>
            <p className="mt-auto pt-3 font-mono text-[7px] tracking-[0.3em] text-stone-600">
              BGNZN.ORG · BOURG MERCHANT EXCHANGE · CHAIN 9940
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
